import React, { lazy, useEffect } from "react";
import "./index.css";
import "./fonts.css";

import ErrorPage from "./error-page";
import Navbar from "./components/nav/nav";
import ProtectedRoutes from "./components/ProtectedRoute/ProtectedRoute";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, CssBaseline } from "@mui/material";
import theme from "./theme/theme";
import { queryClient, setOnUnauthorized } from "./lib/queryClient";
import { Toaster } from "./components/toast/Toaster";
import { useAuthStore } from "./store/authStore";
import { buildAuthRedirectUrl } from "./lib/authRedirect";
import BootSplash from "./components/bootSplash/BootSplash";
import AuthLayout from "./components/authLayout/AuthLayout";

// Route-level code splitting: each page is loaded on demand so the initial
// bundle stays small. The Navbar layout renders a Suspense fallback around the
// Outlet while a page chunk downloads.
const Home = lazy(() => import("./routes/home"));
const SaleslListings = lazy(() => import("./routes/salesListings"));
const RentalListings = lazy(() => import("./routes/rentalListings"));
const RentalListingDetails = lazy(
  () => import("./routes/rentalListings/rentalListingsDetails")
);
const SalesListingDetails = lazy(
  () => import("./routes/salesListings/salesListingsDetails")
);
const RentalAppointment = lazy(
  () => import("./routes/rentalListings/rentalAppointment")
);
const ContactUs = lazy(() => import("./routes/contactUs"));
const About = lazy(() => import("./routes/about"));
const Roadmap = lazy(() => import("./routes/roadmap"));
const AuthForm = lazy(() => import("./routes/authForms/AuthForm"));
const ConfirmEmail = lazy(() => import("./routes/authForms/ConfirmEmail"));
const ForgotPassword = lazy(() => import("./routes/authForms/forgotPassword"));
const ResetPassword = lazy(() => import("./routes/authForms/resetPassword"));
const TenantHome = lazy(() => import("./routes/tenant/home/tenantHome"));
const TenantMaintenance = lazy(
  () => import("./routes/tenant/maintenance/tenantMaintenance")
);
const SignDocument = lazy(
  () => import("./routes/tenant/documents/signDocument")
);
const AddNewProprty = lazy(
  () => import("./routes/manager/properties/addNewProperty")
);
const PropertiesListManager = lazy(
  () => import("./routes/manager/properties/propertyIndex")
);
const PropertyDetails = lazy(
  () => import("./routes/manager/properties/propertyDetails")
);
const EditProperty = lazy(
  () => import("./routes/manager/properties/editProperty")
);
const ViewAllExpenses = lazy(
  () => import("./routes/manager/expenses/viewAllExpenses")
);
const ManagerDashboard = lazy(
  () => import("./routes/manager/dashboard/ManagerDashboard")
);
const AddNewExpense = lazy(
  () => import("./routes/manager/expenses/addNewExpense")
);
const LeasesList = lazy(() => import("./routes/manager/leases/leasesIndex"));
const AddNewLease = lazy(() => import("./routes/manager/leases/addNewLease"));
const AdminOverview = lazy(() => import("./routes/admin"));
const AdminFeatured = lazy(() => import("./routes/admin/featured"));
const AdminProperties = lazy(() => import("./routes/admin/properties"));

// /dashboard resolves to the user's role home (never a shared static page).
const DashboardRedirect: React.FC = () => {
  const roles = useAuthStore((s) => s.roles);
  const currentRole = useAuthStore((s) => s.currentRole);
  const homeRole = currentRole && roles.includes(currentRole) ? currentRole : roles[0];
  const target =
    homeRole === "admin"
      ? "/admin"
      : homeRole === "landlord"
        ? "/manager/dashboard"
        : "/tenant/dashboard";
  return <Navigate to={target} replace />;
};

const router = createBrowserRouter([
  // Full-screen auth routes: no site nav/footer (see AuthLayout).
  {
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/auth",
        element: <AuthForm />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/confirm-email",
        element: <ConfirmEmail />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/forgot-password",
        element: <ForgotPassword />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/reset-password",
        element: <ResetPassword />,
        errorElement: <ErrorPage />,
      },
    ],
  },
  {
    element: <Navbar />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <Home />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/salesListings",
        element: <SaleslListings />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/salesListings/:propertyId",
        element: <SalesListingDetails />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/rentalListings",
        element: <RentalListings />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/rentalListings/:propertyId",
        element: <RentalListingDetails />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/rentalListings/rentalAppointment/:propertyId",
        element: <RentalAppointment />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/contactUs",
        element: <ContactUs />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/about",
        element: <About />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/roadmap",
        element: <Roadmap />,
        errorElement: <ErrorPage />,
      },
      // Auth-only routes: any signed-in user (no role requirement).
      {
        element: <ProtectedRoutes />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "/dashboard",
            element: <DashboardRedirect />,
            errorElement: <ErrorPage />,
          },
        ],
      },
      // Landlord/Owner routes: require the `landlord` role (tenant is redirected home).
      {
        element: <ProtectedRoutes requiredRole="landlord" />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "/manager/dashboard",
            element: <ManagerDashboard />,
            errorElement: <ErrorPage />,
          },
          {
            path: "/properties",
            element: <PropertiesListManager />,
            errorElement: <ErrorPage />,
          },
          {
            path: "/properties/addNewProperty",
            element: <AddNewProprty />,
            errorElement: <ErrorPage />,
          },
          {
            path: "/properties/:propertyId",
            element: <PropertyDetails />,
            errorElement: <ErrorPage />,
          },
          {
            path: "/properties/:propertyId/edit",
            element: <EditProperty />,
            errorElement: <ErrorPage />,
          },
          {
            path: "properties/:propertyId/expenses",
            element: <ViewAllExpenses />,
            errorElement: <ErrorPage />,
          },
          {
            path: "properties/:propertyId/expenses/addNewExpense",
            element: <AddNewExpense />,
            errorElement: <ErrorPage />,
          },
          {
            path: "/manager/leases",
            element: <LeasesList />,
            errorElement: <ErrorPage />,
          },
          {
            path: "/manager/leases/new",
            element: <AddNewLease />,
            errorElement: <ErrorPage />,
          },
        ],
      },
      // Tenant routes: require the `tenant` role (landlord is redirected home).
      {
        element: <ProtectedRoutes requiredRole="tenant" />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "/tenant/dashboard",
            element: <Navigate to="/tenant/home" replace />,
            errorElement: <ErrorPage />,
          },
          {
            path: "/tenant/home",
            element: <TenantHome />,
            errorElement: <ErrorPage />,
          },
          {
            path: "/tenant/maintenance",
            element: <TenantMaintenance />,
            errorElement: <ErrorPage />,
          },
          {
            path: "/tenant/documents/:documentId/sign",
            element: <SignDocument />,
            errorElement: <ErrorPage />,
          },
        ],
      },
      // Admin routes: require the `admin` role.
      {
        element: <ProtectedRoutes requiredRole="admin" />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "/admin",
            element: <AdminOverview />,
            errorElement: <ErrorPage />,
          },
          {
            path: "/admin/featured",
            element: <AdminFeatured />,
            errorElement: <ErrorPage />,
          },
          {
            path: "/admin/properties",
            element: <AdminProperties />,
            errorElement: <ErrorPage />,
          },
        ],
      },
    ],
  },
]);

// Clear the auth session and bounce to /auth when any request returns 401.
// The current page is preserved as the return target so the user lands back
// where they were after re-logging in.
setOnUnauthorized(() => {
  useAuthStore.getState().logout();
  const current = router.state.location;
  if (current.pathname !== "/auth") {
    router.navigate(buildAuthRedirectUrl(current), {
      state: { from: current },
      replace: true,
    });
  } else {
    router.navigate("/auth", { replace: true });
  }
});

// Boot validation runs once per page load (module flag, not effect deps, so
// StrictMode remounts and re-renders can't double-fire the check).
let bootValidationStarted = false;

function App() {
  const session = useAuthStore((s) => s.session);
  const status = useAuthStore((s) => s.status);

  // Validate the persisted session exactly once, silently: a dead token clears
  // without navigating anywhere; a live one restores roles. Logged-out loads
  // (no session) skip straight to content.
  useEffect(() => {
    if (bootValidationStarted) return;
    bootValidationStarted = true;
    void useAuthStore.getState().validateSession();
  }, []);

  // While an unverified session exists, hold the router on a splash instead of
  // rendering trusted UI from a stale JWT (the old homepage → loading → login
  // cascade). Guest loads never see this.
  const pendingBoot =
    !!session && (status === "unknown" || status === "verifying");

  return (
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          {pendingBoot ? <BootSplash /> : <RouterProvider router={router} />}
          <Toaster />
        </QueryClientProvider>
      </ThemeProvider>
    </React.StrictMode>
  );
}

export default App;
